import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { MapPin, CheckCircle, ArrowRight } from "lucide-react";

const cities = [
  { name: "Nashik", stations: 15, status: "coming", highlight: true },
  { name: "Pune", stations: 12, status: "coming" },
  { name: "Mumbai", stations: 8, status: "coming" },
  { name: "Aurangabad", stations: 6, status: "coming" },
  { name: "Nagpur", stations: 4, status: "coming" },
  { name: "Kolhapur", stations: 3, status: "planned" },
  { name: "Satara", stations: 2, status: "planned" },
  { name: "Solapur", stations: 0, status: "planned" },
];

const highways = [
  { name: "Mumbai-Nashik Highway", stations: 8, image: "https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=400&h=200&fit=crop", status: "finalisation" },
  { name: "Nashik-Pune Expressway", stations: 6, status: "finalisation" },
  { name: "Mumbai-Agra Highway", stations: 5, status: "planning" },
  { name: "Nashik-Aurangabad Highway", stations: 4, status: "finalisation" },
];

const Coverage = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  return (
    <section
      id="coverage"
      className="py-24 md:py-32 bg-background-paper relative overflow-hidden"
      data-testid="coverage-section"
    >
      {/* Background Elements */}
      <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px] -translate-x-1/2 -translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24" ref={containerRef}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            Network Coverage
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4">
            EXPANDING ACROSS
            <br />
            <span className="text-primary">MAHARASHTRA</span>
          </h2>
          <p className="text-foreground-muted text-lg mt-6 max-w-2xl mx-auto">
            From the vineyards of Nashik to the highways of Maharashtra, MILE PLUS is
            building the most reliable EV charging network in the state.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Cities Grid */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="font-heading text-2xl text-foreground mb-6 flex items-center gap-3">
              <MapPin className="w-6 h-6 text-primary" />
              Cities We Cover
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {cities.map((city, index) => (
                <motion.div
                  key={city.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.05 }}
                  className={`glass-card relative ${
                    city.highlight
                      ? "border-primary col-span-2 md:col-span-1"
                      : ""
                  }`}
                  data-testid={`city-card-${city.name.toLowerCase()}`}
                >
                  {city.highlight && (
                    <div className="absolute -top-2 -right-2 px-3 py-1 bg-primary text-black text-xs font-bold rounded-full">
                      HQ
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-heading text-lg text-foreground">
                      {city.name}
                    </h4>
                    {city.status === "coming" && (
                      <CheckCircle className="w-5 h-5 text-secondary" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-foreground-muted text-sm">
                      {city.stations > 0
                        ? `${city.stations} Stations`
                        : city.status === "coming"
                        ? "Coming Soon"
                        : "Planned"}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        city.status === "active"
                          ? "bg-primary/10 text-primary"
                          : city.status === "coming"
                          ? "bg-secondary/10 text-secondary"
                          : "bg-white/5 text-foreground-muted"
                      }`}
                    >
                      {city.status === "active"
                        ? "Live"
                        : city.status === "coming"
                        ? "Coming"
                        : "Planned"}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Highway Coverage */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h3 className="font-heading text-2xl text-foreground mb-6 flex items-center gap-3">
              <ArrowRight className="w-6 h-6 text-secondary" />
              Highway Coverage
            </h3>
            <div className="space-y-4">
              {highways.map((highway, index) => (
                <motion.div
                  key={highway.name}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="glass-card relative"
                  data-testid={`highway-card-${index}`}
                >
                  <div className="absolute top-1 right-5">
                    {highway.status === "planning" ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-secondary/10 text-secondary">
                        Planning
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        Finalisation Stage
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-foreground">
                      {highway.name}
                    </h4>
                    <span className="text-secondary font-heading">
                      {highway.stations} Stations
                    </span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={isInView ? { width: `${(highway.stations / 10) * 100}%` } : {}}
                      transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                      className="h-full rounded-full bg-gradient-to-r from-primary to-secondary"
                    />
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Stats */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="glass-card text-center">
                <p className="font-heading text-4xl text-primary">250+</p>
                <p className="text-foreground-muted text-sm mt-1">
                  Highway KMs Covered
                </p>
              </div>
              <div className="glass-card text-center">
                <p className="font-heading text-4xl text-secondary">30 min</p>
                <p className="text-foreground-muted text-sm mt-1">
                  Avg. Station Distance
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 glass-card text-center"
        >
          <h3 className="font-heading text-2xl text-foreground mb-3">
            Want MILE PLUS in Your City?
          </h3>
          <p className="text-foreground-muted mb-6">
            We're expanding rapidly. Let us know where you'd like to see MILE PLUS
            charging stations next.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
            data-testid="coverage-request-cta"
          >
            Request Coverage
            <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Coverage;
